/**
 * Device App Update API Client
 * Admin endpoints for managing app updates
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Get CSRF token from cookie
 * The token is stored in a non-httpOnly cookie for client-side access
 */
function getCsrfToken() {
  const cookieName = 'csrf-token';
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName) {
      return decodeURIComponent(value);
    }
  }

  return null;
}

/**
 * Get headers with CSRF token for mutating requests
 */
function getHeadersWithCsrf() {
  const headers = {
    'Content-Type': 'application/json'
  };

  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  return headers;
}

/**
 * Get all update versions with adoption stats
 */
export async function getAllUpdates() {
  const response = await fetch(`${API_BASE_URL}/api/admin/device-app-updates`, {
    method: 'GET',
    credentials: 'include', // Include cookies for admin auth
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch updates');
  }

  return response.json();
}

/**
 * Create new update version
 */
export async function createUpdate(data) {
  const response = await fetch(`${API_BASE_URL}/api/admin/device-app-updates`, {
    method: 'POST',
    credentials: 'include',
    headers: getHeadersWithCsrf(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create update');
  }

  return response.json();
}

/**
 * Update rollout configuration for a version
 */
export async function updateRollout(versionCode, data) {
  const response = await fetch(`${API_BASE_URL}/api/admin/device-app-updates/${versionCode}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: getHeadersWithCsrf(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update rollout');
  }

  return response.json();
}

/**
 * Activate a version (deactivates all others)
 * Used for rollback or switching active version
 */
export async function activateVersion(versionCode) {
  const response = await fetch(`${API_BASE_URL}/api/admin/device-app-updates/${versionCode}/activate`, {
    method: 'PATCH',
    credentials: 'include',
    headers: getHeadersWithCsrf()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to activate version');
  }

  return response.json();
}

/**
 * Deactivate an ACTIVE version
 */
export async function deactivateVersion(versionCode) {
  const response = await fetch(`${API_BASE_URL}/api/admin/device-app-updates/${versionCode}/deactivate`, {
    method: 'PATCH',
    credentials: 'include',
    headers: getHeadersWithCsrf()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to deactivate version');
  }

  return response.json();
}

/**
 * Archive a version (cannot archive ACTIVE versions)
 */
export async function archiveVersion(versionCode) {
  const response = await fetch(`${API_BASE_URL}/api/admin/device-app-updates/${versionCode}/archive`, {
    method: 'PATCH',
    credentials: 'include',
    headers: getHeadersWithCsrf()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to archive version');
  }

  return response.json();
}

/**
 * Unarchive a version (restore ARCHIVED to INACTIVE)
 */
export async function unarchiveVersion(versionCode) {
  const response = await fetch(`${API_BASE_URL}/api/admin/device-app-updates/${versionCode}/unarchive`, {
    method: 'PATCH',
    credentials: 'include',
    headers: getHeadersWithCsrf()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to unarchive version');
  }

  return response.json();
}

/**
 * Get devices update status with optional filtering
 */
export async function getDevicesUpdateStatus(filters = {}) {
  const params = new URLSearchParams();
  
  if (filters.status) params.append('status', filters.status);
  if (filters.tenantId) params.append('tenantId', filters.tenantId);
  if (filters.limit) params.append('limit', filters.limit.toString());

  const url = `${API_BASE_URL}/api/admin/devices/update-status${params.toString() ? '?' + params.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch devices status');
  }

  return response.json();
}

/**
 * Get last kiosk app update logs for a device
 */
export async function getDeviceUpdateLogs(deviceId, limit = 10) {
  const params = new URLSearchParams();
  if (limit) params.append('limit', String(limit));

  const url = `${API_BASE_URL}/api/admin/devices/${deviceId}/update-logs${params.toString() ? '?' + params.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Failed to fetch device update logs');
  }

  return response.json();
}

/**
 * Get update logs for a specific version
 */
export async function getUpdateLogs(versionCode, filters = {}) {
  const params = new URLSearchParams();
  
  if (filters.status) params.append('status', filters.status);
  if (filters.limit) params.append('limit', filters.limit.toString());

  const url = `${API_BASE_URL}/api/admin/device-app-updates/${versionCode}/logs${params.toString() ? '?' + params.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch update logs');
  }

  return response.json();
}

/**
 * Update version metadata (only INACTIVE/ARCHIVED)
 */
export async function updateVersion(versionCode, updateData) {
  const response = await fetch(`${API_BASE_URL}/api/admin/device-app-updates/${versionCode}/edit`, {
    method: 'PATCH',
    credentials: 'include',
    headers: getHeadersWithCsrf(),
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update version');
  }

  return response.json();
}

/**
 * Delete version permanently (only INACTIVE/ARCHIVED)
 */
export async function deleteVersion(versionCode) {
  const response = await fetch(`${API_BASE_URL}/api/admin/device-app-updates/${versionCode}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getHeadersWithCsrf()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete version');
  }

  return response.json();
}

/**
 * Get global update settings (maintenance window)
 */
export async function getUpdateSettings() {
  const response = await fetch(`${API_BASE_URL}/api/admin/device-app-updates/settings`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch update settings');
  }

  return response.json();
}

/**
 * Update global update settings (maintenance window)
 * Body: { maintenanceWindow: { enabled, startUtc, endUtc } }
 */
export async function updateUpdateSettings(data) {
  const response = await fetch(`${API_BASE_URL}/api/admin/device-app-updates/settings`, {
    method: 'PUT',
    credentials: 'include',
    headers: getHeadersWithCsrf(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Failed to update update settings');
  }

  return response.json();
}
